import React, { useEffect, useRef, useState } from 'react';
import { Accordion, Breadcrumb, Dropdown, Tab, Tabs } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { BiLogOut } from "react-icons/bi";
import { BsEye, BsThreeDotsVertical, BsXLg } from "react-icons/bs";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import AIReportChat from '../../../components/AI/AIReportChat';
import AITextGenWithPrompt from '../../../components/AI/AITextGenWithPrompt';
import HTMLEditor from '../../../components/Editor/HTMLEditor';
import { ReportPageType, ReportSection } from '../../../core/constants/common';
import ReportingUtils from '../../../core/utilities/ReportingUtils';
import { buildReportingListUrl } from '../../../core/utilities/reportingListUrl';
import esg_ai_W from '../../../images/esg_ai.png';
import esg_ai from '../../../images/esg_ai_Green.png';
import GRI_logo from '../../../images/global-reporting.png';
import CompanyGraphCard from '../CompanyDashboardGraph/CompanyGraphCard';
import { beautifyReportHtml } from './utils/htmlBeautifier';
import './Reporting.scss';

const BASE_URL_DOMAIN = `${window.location.protocol}//${window.location.host}`;

const ReportingCard = ({
	t,
	companyId,
	reportName,
	page,
	section,
	source = 'reporting-list',
	isPreview,
	onSave,
	onFinalize,
	onCancel,
	onMetricValueChange,
	onContentEdited,
	reportId,
	yearFilter,
	regulationId,
	regulationName,
	regulationFrequency,
	onPreview,
	reportType,
	onReportingPeriodChange,
	jobId,
	loader,
	reportingStandard,
	language,
	periodStart,
	periodEnd,
	entities,
	onPageChange,
	sections,
	getContentRef,
	activeTab,
	listPage,
	listSize
}) => {

	const [edited, setEdited] = useState(false);
	const [saveEnabled, setSaveEnabled] = useState(false);
	const [showAITextGen, setShowAITextGen] = useState(false);
	const [periodYear, setPeriodYear] = useState(yearFilter);
	const [pageHTML, setPageHTML] = useState(page.html);
	const editorRef = useRef(null);
	const navigate = useNavigate();

	const goToReportingList = () => {
		navigate(buildReportingListUrl({
			view: reportingStandard,
			page: activeTab,
			listPage,
			size: listSize,
		}));
	};

	const [showTips, setShowTips] = useState(false);
	const [additionalClass, setAdditionalClass] = useState('');
	const [showDesign, setShowDesign] = useState(false);
	const [showContentBlock, setShowContentBlock] = useState(false);
	const [showFullView, setShowFullView] = useState(true);
	const addRef = useRef(null);
	const cardScrollRef = useRef(null);
	const [showDashboardGraphs, setShowDashboardGraphs] = useState(false);
	const [selectedGraph, setSelectedGraph] = useState(null);
	const exportRef = useRef(null);
	const [showCopilotHistory, setShowCopilotHistory] = useState(false);

	useEffect(() => {
		setPageHTML(page.html);
	}, [page.html]);

	useEffect(() => {
		if (!getContentRef) {
			return undefined;
		}
		getContentRef.current = () => ({
			html: edited && editorRef?.current
				? editorRef.current.getContent()
				: (pageHTML ?? page.html),
			json: page.json
		});
		return () => {
			getContentRef.current = null;
		};
	}, [edited, pageHTML, page.html, page.json, getContentRef]);

	useEffect(() => {
		toggleVisibility('Tips')
	}, []);

	// Ensure the top of the editor/card is visible whenever the active page changes
	useEffect(() => {
		if (cardScrollRef.current) {
			// Compute absolute top of the card and account for sticky header height
			const rect = cardScrollRef.current.getBoundingClientRect();
			const headerOffset = 120; // approximate height of the sticky header/toolbar
			const targetTop = window.scrollY + rect.top - headerOffset;
			window.scrollTo({ top: targetTop, behavior: 'smooth' });
		} else {
			// Fallback: scroll window to top
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page?.id, page?.index]);

	const handleContentsAreaClick = (e) => {
		if (onPageChange && sections) {
			const target = ReportingUtils.resolveGriTocPageNavigation(e.target, sections);
			if (target) {
				e.preventDefault();
				e.stopPropagation();
				onPageChange(target.sIndex, target.pIndex);
			}
		}
	};

	const isTocPage = page?.type === ReportPageType.CONTENTS;

	const createMarkup = () => {
		return { __html: page.html };
	}
	const getPagetoRender = () => {
		if (page?.type === ReportPageType.TITLE) {
			return <>
				<div className='Reporting_CardBox RC_Fristone 1 HtmlEditorBox' onClick={() => onCardClick()}>
					<div className='card RC_card portrait'>
						<div className='card-body'>
							<div className='RC_Fristone_inner'>
								{
									edited
										? <HTMLEditor editorRef={editorRef} initialValue={pageHTML}
											onContentChange={() => {
												setSaveEnabled(true);
												onContentEdited();
											}} />
										: <div dangerouslySetInnerHTML={createMarkup()}></div>
								}
							</div>
						</div>
					</div>
				</div>
			</>;
		} else if (page?.type === ReportPageType.CONTENTS) {
			return <>
				<div className='Reporting_CardBox RC_Commen_card gri-toc-card gri-toc-readonly' onClick={handleContentsAreaClick}>
					<div className='card RC_card portrait'>
						<div className='card-body'>
							<div className='RC_Commen_card_inner'>
								<div className='RC_Commen_card_inner_report' dangerouslySetInnerHTML={createMarkup()}></div>
							</div>
							<div className="Rc_Card_footer">
								<p>{reportName}</p>
								<div>
									{/* {page?.pageNumber || 1} */}
								</div>
							</div>
						</div>
					</div>
				</div>
			</>
		} else {
			return <>
				<div className='Reporting_CardBox  RC_Commen_card HtmlEditorBox 1' onClick={() => onCardClick()}>
					<div className='card RC_card portrait'>
						<div className='card-body'>
							{renderESGAIRibbon()}
							<div className='RC_Commen_card_inner'>
								{
									edited
										? <HTMLEditor editorRef={editorRef} initialValue={pageHTML}
											onContentChange={(e) => {
												setSaveEnabled(true);
												onContentEdited();
											}} />
										: <div dangerouslySetInnerHTML={createMarkup()} className='RC_Commen_card_inner_report'></div>
								}
							</div>
							<div ref={addRef} />

							<div className="Rc_Card_footer">
								<p>{reportName}</p>
								<div>
									{/* {page?.pageNumber || 1} */}
								</div>
							</div>
						</div>
					</div>
				</div>
			</>;
		}
	}

	const scrollToLastSection = () => {
		addRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const getCardActions = () => {
		const hideActions = isPreview;

		return <>
			<div className='floating-action-panel row'>

				<div className='col-md-5'>
					<Breadcrumb>
						<Breadcrumb.Item onClick={() => {
							if (source === 'ghg-report') {
								navigate(`/company_admin/ghg_inventory_report`);
							} else {
								goToReportingList();
							}
						}} > {source === 'ghg-report' ? t("GHGReport.label") : t("ReportingStudio.label")}</Breadcrumb.Item>
						<Breadcrumb.Item active>{reportName}</Breadcrumb.Item>
					</Breadcrumb>
				</div>

				<div className='framework_imgbox col-md-1'>
					<img src={GRI_logo} />
				</div>
				<div className='action-holder RS_btn_head col-md-6'>
					{
						!hideActions &&
						<div>
							{!edited && reportId > 0 &&
								<button className='btn btn-light me-2'
									onClick={() => {
										onPreview();
										setEdited(false);
									}}>
									<BsEye /> {t("Preview.label")}
								</button>
							}
							{
								edited &&
								<button className='btn btn-outline-secondary me-2 px-2'
									onClick={handleBeautify}>
									{t("Beautify.label")}
								</button>
							}

							{
								edited &&
								<button className='btn btn-outline-primary me-2 px-2'
									disabled={edited && !saveEnabled}
									onClick={() => {
										onSave(edited ? editorRef?.current?.getContent() : page.html, page.json);
										setEdited(false);
									}}>
									{t("SaveDraft.label")}
								</button>
							}

							{
								edited &&
								<button className='btn btn-secondary me-2 px-2'
									onClick={() => {
										setEdited(false);
										onCancel();
									}}>
									{t("Cancel.label")}
								</button>
							}


							{showFullView && !isLeadershipMessagePage() &&
								<button className='btn btn-secondary me-2 px-2'
									onClick={() => {
										setShowFullView(false);
									}}>{t("ESGAICopilot.label")}
								</button>
							}
						</div>
					}
					<button className='btn btn-primary'
						onClick={() => {
							if (source === 'ghg-report') {
								navigate(`/company_admin/ghg_inventory_report`);
							} else {
								goToReportingList();
							}
						}}>
						<BiLogOut /> {t("Exit.label")}
					</button>
				</div>

			</div>
		</>;
	};

	const canShowTextGenIcon = () => {
		const validPageTypes = [
			ReportPageType.ABOUT_REPORT,
			ReportPageType.ABOUT_COMPANY,
			ReportPageType.ENERGY,
			ReportPageType.CARBON_EMISSION,
			ReportPageType.WASTE,
			ReportPageType.WATER,
			ReportPageType.ENV_OVERVIEW,
			ReportPageType.CUSTOMERS,
			ReportPageType.COMMUNITY,
			ReportPageType.EMPLOYEES,
			ReportPageType.SUPPLIERS,
			ReportPageType.SOCIAL_OVERVIEW,
			ReportPageType.ESG_GOV,
			ReportPageType.BOARD_GOV,
			ReportPageType.HUMAN_RIGHTS,
			ReportPageType.ETHICS_COMP,
			ReportPageType.TAX_POLICY,
			ReportPageType.GOV_OVERVIEW,
			ReportPageType.ESG_GOALS,
			ReportPageType.ESG_HIGHLIGHTS,
			ReportPageType.ENVIRONMENT,
			ReportPageType.SOCIAL,
			ReportPageType.GOVERNANCE
		];

		return (
			validPageTypes.includes(page.type) && showAITextGen
		);
	};
	const transferTextToEditor = (text, jsonData = null) => {
		setEdited(true);
		onContentEdited();
		let htmlString = '';

		// Check if text is italic-wrapped HTML from AI transfer
		if (text && typeof text === 'string' && text.includes('<p><em>')) {
			htmlString = text;
		} else if (text && typeof text === 'string' && text.includes('container-fluid') && text.includes('flex-wrap')) {
			// Text is already formatted HTML from convertESGHighlightsToHTML
			htmlString = text;
		} else if (jsonData) {
			// Use the old table-based formatting for backward compatibility
			htmlString = ReportingUtils.getESGHighlightsCardBasedHTML(jsonData);
		} else {
			htmlString = ReportingUtils.getHTMLWithTextFormattedAsParagraphs({
				text: text,
			});
		}

		setSaveEnabled(true);
		// if (edited && editorRef?.current?.getContent()) {
		// 	// If we are already in edit mode, append to the edited content
		// 	setPageHTML(editorRef?.current?.getContent() + htmlString);
		if (edited && editorRef?.current) {
			// If we are already in edit mode, insert at cursor position
			setPageHTML(editorRef?.current?.insertContent(htmlString));
			//scrollToLastSection();
		} else {
			// Otherwise append to the saved page content
			setPageHTML(pageHTML + htmlString);
		}
	};

	const addContentBlockDetails = (name, description) => {
		setEdited(true);
		onContentEdited();
		let htmlString = '';
		htmlString = ReportingUtils.getHTMLWithContentBlock({
			name: name,
			description: description
		});
		setSaveEnabled(true);
		// if (edited && editorRef?.current?.getContent()) {
		// 	// If we are already in edit mode, append to the edited content
		// 	setPageHTML(editorRef?.current?.getContent() + htmlString);
		// 	scrollToLastSection();
		if (edited && editorRef?.current) {
			// If we are already in edit mode, insert at cursor position
			setPageHTML(editorRef?.current?.insertContent(htmlString));
			scrollToLastSection();
		} else {
			// Otherwise append to the saved page content
			setPageHTML(pageHTML + htmlString);
			scrollToLastSection();
		}
	}

	const addGraphImageToEditor = (graphImageURL) => {
		setSelectedGraph(null);
		setEdited(true);
		onContentEdited();
		let htmlString = `<img src="${graphImageURL}" width="680px" />`;
		setSaveEnabled(true);
		if (edited && editorRef?.current) {
			// If we are already in edit mode, append to the edited content
			setPageHTML(editorRef?.current?.insertContent(htmlString));
			scrollToLastSection();
		} else {
			// Otherwise append to the saved page content
			setPageHTML(pageHTML + htmlString);
			scrollToLastSection();
		}
	}

	const contentToEditor = (data, jsonData = null) => {
		setEdited(true);
		onContentEdited();
		let htmlString = data;
		setSaveEnabled(true);
		// if (edited && editorRef?.current?.getContent()) {
		// 	// If we are already in edit mode, append to the edited content
		// 	setPageHTML(editorRef?.current?.getContent() + htmlString);
		if (edited && editorRef?.current) {
			// If we are already in edit mode, insert at cursor position
			setPageHTML(editorRef?.current?.insertContent(htmlString));
			//scrollToLastSection();
		} else {
			// Otherwise append to the saved page content
			setPageHTML(pageHTML + htmlString);
		}
	};

	const renderESGAIRibbon = () => {
		if (!page.aiGenerated) return <></>;
		return (
			<div className='generated_AI_tag'>
				<img src={esg_ai_W} />
				<b>{t('ESGAICopilot.label')}</b>

			</div>
		);
	};

	const onCardClick = () => {
		if (isTocPage) return;
		setEdited(true);
		//setShowFullView(false);  to hide the AI copilot panel to show on click of editor
	};

	const handleBeautify = () => {
		try {
			const currentHtml = (editorRef?.current?.getContent() ?? pageHTML ?? page.html) || '';
			const beautified = beautifyReportHtml(currentHtml);
			editorRef?.current?.setContent(beautified);
			setPageHTML(beautified);
			setSaveEnabled(true);
			onContentEdited();
			toast.success(t("Beautify.success.label"));
		} catch (err) {
			toast.error(t("Beautify.error.label"));
		}
	};

	const canRenderTipsCard = () => {
		if (section?.type !== ReportSection.ROOT
			&& section?.type !== ReportSection.TITLE
			&& section?.type !== ReportSection.CONTENTS
			&& section?.type !== ReportSection.LEADERSHIP
			&& section?.type !== ReportSection.ABOUT_REPORT
			&& section?.type !== ReportSection.ABOUT_COMPANY
			&& section?.type !== ReportSection.ESG_APPROACH
			&& section?.type !== ReportSection.ENVIRONMENT
			&& section?.type !== ReportSection.SOCIAL
			&& section?.type !== ReportSection.GOVERNANCE
		) return false;

		return true;
	}

	const toggleVisibility = (type) => {
		switch (type) {
			case 'AI':
				setShowTips(false);
				setShowAITextGen(true);
				setShowContentBlock(false);
				setShowDesign(false);
				setShowDashboardGraphs(false);
				break;
			case 'Tips':
				setShowTips(true);
				setShowAITextGen(false);
				setShowContentBlock(false);
				setShowDesign(false);
				setShowDashboardGraphs(false);
				break;
			case 'Design':
				setShowTips(false);
				setShowAITextGen(false);
				setShowContentBlock(false);
				setShowDesign(true);
				setShowDashboardGraphs(false);
				break;
			case 'Content':
				setShowTips(false);
				setShowAITextGen(false);
				setShowContentBlock(true);
				setShowDesign(false);
				setShowDashboardGraphs(false);
				break;
			case 'Graph':
				setShowTips(false);
				setShowAITextGen(false);
				setShowContentBlock(false);
				setShowDesign(false);
				setShowDashboardGraphs(true);
				break;
			default:
				setShowTips(false);
				setShowAITextGen(false);
				setShowContentBlock(false);
				setShowDesign(false);
				setShowDashboardGraphs(false);
				break;
		}

	};

	const renderAIChat = () => {
		return (
			<>
				{
					<div className="Report_ESGAICopilot">
						<Tabs
							defaultActiveKey={canShowTextGenIcon() ? 'compose' : 'chat'}
							id="Report_ESGAICopilot_box"
						>
							{canShowTextGenIcon() &&
								<Tab eventKey="compose" title={t("Compose.label")}>
									<div className='p-0'>
										<AITextGenWithPrompt page={page}
											section={section}
											periodYear={periodYear}
											regulationId={regulationId}
											regulationName={regulationName}
											regulationFrequency={regulationFrequency}
											onHide={() => setShowAITextGen(false)}
											onTransferToEditor={(text, jsonData) => transferTextToEditor(text, jsonData)}
											jobId={jobId} reportingStandard={reportingStandard}
											language={language} reportName={reportName} />
									</div>
								</Tab>
							}
							<Tab eventKey="chat" title={t('Chat.label')}>
								<div>
									<AIReportChat onTransferToEditor={(text, jsonData) => contentToEditor(text, jsonData)} />
								</div>
							</Tab>
						</Tabs>
					</div>
				}
			</>
		);
	}

	const renderTipsCard = () => {
		return (
			<>
				{
					<div className='Tips_cardBox'>
						<div className='Tips_cardBox-header'>
							<h5 className="m-0">Tips</h5>
						</div>
						<div className='card-body p-2'>
							{
								section?.type === ReportSection.TITLE &&
								<div className='Leadershipmessages_Card'>
									<span className='p-1'>Tips not available for Title section</span>
								</div>
							}
							{
								section?.type === ReportSection.CONTENTS &&
								<div className='Leadershipmessages_Card'>
									<span className='p-1'>Tips not available for Contents section</span>
								</div>
							}
							{
								section?.type === ReportSection.LEADERSHIP &&
								<div className='Leadershipmessages_Card'>
									<Accordion defaultActiveKey={['0']} alwaysOpen>
										<Accordion.Item eventKey="0">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key4")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key5")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="1">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key6")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key7")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="2">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key8")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key9")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="3">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key10")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key11")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="4">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key12")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key13")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="5">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key14")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key15")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="6">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key16")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key17")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="8">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key18")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key19")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="9">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key20")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key21")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="10">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key22")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key23")}</p>
											</Accordion.Body>
										</Accordion.Item>

										<Accordion.Item eventKey="11">
											<Accordion.Header>
												{t("LeadershipmessagesTips.key24")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("LeadershipmessagesTips.key25")}</p>
											</Accordion.Body>
										</Accordion.Item>
									</Accordion>
								</div>
							}
							{
								section?.type === ReportSection.ABOUT_REPORT &&
								<div className='AbouttheReport_Card'>
									{
										page?.type === ReportPageType.ABOUT_REPORT &&
										<div className='AbouttheReport_Card1'>
											<Accordion>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("AbouttheReport.key4")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("AbouttheReport.key5")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("AbouttheReport.key6")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("AbouttheReport.key7")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("AbouttheReport.key8")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("AbouttheReport.key9")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("AbouttheReport.key10")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("AbouttheReport.key11")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("AbouttheReport.key12")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("AbouttheReport.key13")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("AbouttheReport.key14")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("AbouttheReport.key15")}</p>
														<p>{t("AbouttheReport.key17")}</p>
													</Accordion.Body>
												</Accordion.Item>


											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.ESG_HIGHLIGHTS &&
										<div className='AbouttheReport_Card2'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("AbouttheReport.key18")}
													</Accordion.Header>
													<Accordion.Body>
														{/* <p>{t("AbouttheReport.key19")}</p> */}
														<p>{t("AbouttheReport.key20")}</p>
														<p>{t("AbouttheReport.key21")}</p>
														<p>{t("AbouttheReport.key22")}</p>
														<p>{t("AbouttheReport.key23")}</p>
														<p>{t("AbouttheReport.key24")}</p>
														<p>{t("AbouttheReport.key25")}</p>
														<p>{t("AbouttheReport.key26")}</p>
														<p>{t("AbouttheReport.key27")}</p>
														<p>{t("AbouttheReport.key28")}</p>
														<p>{t("AbouttheReport.key29")}</p>
														<div>
															<div className='row  m-0'>
																<div className='col-md-6 p-2'>
																	<div className='card text-center'>
																		<div className='card-body'>
																			<h6>{t("AbouttheReport.key30")}</h6>
																			<b>{t("AbouttheReport.key31")}</b>
																			<p>{t("AbouttheReport.key32")}</p>
																		</div>
																	</div>
																</div>
																<div className='col-md-6 p-2'>
																	<div className='card text-center'>
																		<div className='card-body'>
																			<h6>{t("AbouttheReport.key33")}</h6>
																			<b>{t("AbouttheReport.key31")}</b>
																			<p>{t("AbouttheReport.key32")}</p>
																		</div>
																	</div>
																</div>
																<div className='col-md-6 p-2'>
																	<div className='card text-center'>
																		<div className='card-body'>
																			<h6>100%</h6>
																			<b>{t("AbouttheReport.key31")}</b>
																			<p>{t("AbouttheReport.key32")}</p>
																		</div>
																	</div>
																</div>
																<div className='col-md-6 p-2'>
																	<div className='card text-center'>
																		<div className='card-body'>
																			<h6>{t("AbouttheReport.key34")}</h6>
																			<b>{t("AbouttheReport.key31")}</b>
																			<p>{t("AbouttheReport.key32")}</p>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													</Accordion.Body>
												</Accordion.Item>


											</Accordion>
										</div>
									}
								</div>
							}
							{
								page?.type === ReportPageType.ABOUT_COMPANY &&
								<div className='AbouttheCompany_Card'>
									<Accordion defaultActiveKey={['0']} alwaysOpen>
										<Accordion.Item eventKey="0">
											<Accordion.Header>
												{t("AbouttheCompany.key2")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("AbouttheCompany.key4")}</p>
												<p>{t("AbouttheCompany.key5")}</p>
												<p>{t("AbouttheCompany.key6")}</p>
												<p>{t("AbouttheCompany.key7")}</p>
												<p>{t("AbouttheCompany.key8")}</p>
												<p>{t("AbouttheCompany.key9")}</p>
												<p>{t("AbouttheCompany.key10")}</p>
												<p>{t("AbouttheCompany.key12")}</p>
												<p>{t("AbouttheCompany.key13")}</p>
												<p>{t("AbouttheCompany.key14")}</p>
												<p>{t("AbouttheCompany.key15")}</p>
												<p>{t("AbouttheCompany.key16")}</p>
											</Accordion.Body>
										</Accordion.Item>

									</Accordion>
								</div>
							}
							{
								section?.type === ReportSection.ESG_APPROACH &&
								<div className='OurESGApproach_Card'>
									{
										page?.type === ReportPageType.OTHERS &&
										<div className='KeyStakeholderIssues_Card  '>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("OurESGApproach.key4")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key5")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("OurESGApproach.key6")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key7")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("OurESGApproach.key8")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key9")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("OurESGApproach.key10")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key11")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("OurESGApproach.key12")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key13")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("OurESGApproach.key15")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key16")}</p>
														<p>{t("OurESGApproach.key17")}</p>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.ESG_GOALS &&
										<div className='ESGGoals_Card '>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("OurESGApproach.key4")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key19")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("OurESGApproach.key20")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key21")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("OurESGApproach.key22")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key23")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("OurESGApproach.key24")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key25")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("OurESGApproach.key26")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key27")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("OurESGApproach.key29")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key30")}</p>
													</Accordion.Body>
												</Accordion.Item>
												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("OurESGApproach.key31")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("OurESGApproach.key32")}</p>
														<p>{t("OurESGApproach.key33")}</p>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										</div>
									}
								</div>
							}
							{
								section?.type === ReportSection.ENVIRONMENT &&
								<div className='Environment_Card'>
									{
										page?.type === ReportPageType.ENV_OVERVIEW &&
										<div className='Overview_Card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Environment.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key3")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Environment.key4")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key5")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Environment.key6")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key7")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Environment.key8")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key9")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Environment.key10")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key11")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Environment.key12")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key13")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Environment.key14")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key15")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Environment.key16")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key17")}</p>
													</Accordion.Body>
												</Accordion.Item>
												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Environment.key18")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key19")}</p>
														<p>{t("Environment.key20")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.ENERGY &&
										<div className='Energy_Card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Environment.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key23")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Environment.key24")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key25")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Environment.key26")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key27")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Environment.key28")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key29")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Environment.key30")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key31")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Environment.key34")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key35")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Environment.key36")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key37")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Environment.key38")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key39")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Environment.key40")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key41")}</p>
														<p>{t("Environment.key42")}</p>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.CARBON_EMISSION &&
										<div className='Carbon_EmissionsCard'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Environment.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key44")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Environment.key45")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key46")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Environment.key47")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key48")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Environment.key49")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key50")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Environment.key51")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key52")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Environment.key53")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key54")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Environment.key55")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key56")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Environment.key40")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key57")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Environment.key58")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key59")}</p>
														<p>{t("Environment.key60")}</p>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.WASTE &&
										<div className='Waste_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Environment.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key62")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Environment.key63")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key64")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Environment.key65")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key66")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Environment.key67")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key68")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Environment.key69")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key70")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Environment.key71")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key72")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Environment.key73")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key74")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Environment.key75")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key39")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Environment.key76")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key77")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Environment.key78")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key79")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.WATER &&
										<div className='Waste_card '>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Environment.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key82")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Environment.key83")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key84")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Environment.key12")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key85")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Environment.key86")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key87")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Environment.key88")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key89")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Environment.key90")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key91")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Environment.key92")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key93")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Environment.key94")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key95")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Environment.key38")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key39")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Environment.key40")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Environment.key96")}</p>
														<p>{t("Environment.key97")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
								</div>
							}
							{
								section?.type === ReportSection.SOCIAL &&
								<div className='Social_card'>
									{
										page?.type === ReportPageType.SOCIAL_OVERVIEW &&
										<div className='Overview_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Social.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key3")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Social.key4")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key5")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Social.key6")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key7")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Social.key8")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key9")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Social.key10")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key11")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Social.key12")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key13")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Social.key14")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key15")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Social.key16")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key17")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Social.key18")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key19")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Social.key20")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key21")}</p>
														<p>{t("Social.key22")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.CUSTOMERS &&
										<div className='Customers_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Social.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key26")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Social.key27")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key28")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Social.key29")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key30")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Social.key31")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key32")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Social.key33")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key34")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Social.key35")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key36")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Social.key37")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key38")}</p>
														<p>{t("Social.key39")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.COMMUNITY &&
										<div className='Community_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Social.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key43")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Social.key44")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key45")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Social.key46")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key47")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Social.key48")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key49")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Social.key50")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key51")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Social.key52")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key53")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Social.key54")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key55")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Social.key56")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key57")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Social.key58")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key59")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Social.key60")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key61")}</p>
														<p>{t("Social.key62")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.EMPLOYEES &&
										<div className='Employees_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Social.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key65")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Social.key66")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key67")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Social.key68")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key69")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Social.key70")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key71")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Social.key72")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key73")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Social.key74")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key75")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Social.key76")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key77")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Social.key78")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key79")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Social.key80")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key81")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Social.key82")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key83")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Social.key35")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key84")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.SUPPLIERS &&
										<div className='Suppliers_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Social.key86")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key88")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Social.key89")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key90")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Social.key91")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key92")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Social.key93")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key94")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Social.key95")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key96")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Social.key97")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key98")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Social.key99")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key100")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Social.key101")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key102")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Social.key103")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key104")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Social.key105")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Social.key106")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
								</div>
							}
							{
								section?.type === ReportSection.GOVERNANCE &&
								<div className='Governance_card'>
									{
										page?.type === ReportPageType.GOV_OVERVIEW &&
										<div className='Overview_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Governance.key2")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key3")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Governance.key4")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key5")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Governance.key6")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key7")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Governance.key8")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key9")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Governance.key10")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key11")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Governance.key12")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key13")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Governance.key14")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key15")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Governance.key16")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key17")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Governance.key18")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key19")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Governance.key21")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key22")}</p>
														<p>{t("Governance.key23")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.ESG_GOV &&
										<div className='ESG_Governance_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Governance.key26")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key27")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Governance.key28")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key29")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Governance.key30")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key31")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Governance.key32")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key33")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Governance.key34")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key35")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Governance.key36")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key37")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Governance.key38")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key39")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Governance.key40")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key41")}</p>
														<p>{t("Governance.key42")}</p>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.BOARD_GOV &&
										<div className='BoardGovernance_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Governance.key45")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key46")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Governance.key47")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key48")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Governance.key49")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key50")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Governance.key51")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key52")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Governance.key53")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key54")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Governance.key55")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key56")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Governance.key57")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key58")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Governance.key59")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key60")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Governance.key61")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key62")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Governance.key63")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key64")}</p>
														<p>{t("Governance.key65")}</p>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.HUMAN_RIGHTS &&
										<div className='HumanRights_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Governance.key68")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key69")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Governance.key70")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key71")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Governance.key72")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key73")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Governance.key74")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key75")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Governance.key76")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key77")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Governance.key78")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key79")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Governance.key80")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key81")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Governance.key82")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key83")}</p>
														<p>{t("Governance.key84")}</p>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.ETHICS_COMP &&
										<div className='EthicsCompliance_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Governance.key85")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key87")}</p>
														<p>{t("Governance.key88")}</p>
														<p>{t("Governance.key89")}</p>
														<p>{t("Governance.key90")}</p>
														<p>{t("Governance.key91")}</p>
														<p>{t("Governance.key92")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Governance.key93")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key94")}</p>
														<p>{t("Governance.key95")}</p>
														<p>{t("Governance.key96")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.TAX_POLICY &&
										<div className='TaxPolicy_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Governance.key99")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key100")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Governance.key101")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key102")}</p>
														<p>{t("Governance.key103")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Governance.key104")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key105")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Governance.key106")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key107")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Governance.key108")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key109")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Governance.key110")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key111")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Governance.key112")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key113")}</p>
														<p>{t("Governance.key114")}</p>
													</Accordion.Body>
												</Accordion.Item>

											</Accordion>
										</div>
									}
									{
										page?.type === ReportPageType.BC_DIS_RECOVERY &&
										<div className='BusinessContinuity_card'>
											<Accordion defaultActiveKey={['0']} alwaysOpen>
												<Accordion.Item eventKey="0">
													<Accordion.Header>
														{t("Governance.key117")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key118")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="1">
													<Accordion.Header>
														{t("Governance.key119")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key120")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="2">
													<Accordion.Header>
														{t("Governance.key121")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key122")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="3">
													<Accordion.Header>
														{t("Governance.key123")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key124")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="4">
													<Accordion.Header>
														{t("Governance.key125")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key126")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="5">
													<Accordion.Header>
														{t("Governance.key127")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key128")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="6">
													<Accordion.Header>
														{t("Governance.key129")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key130")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="7">
													<Accordion.Header>
														{t("Governance.key131")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key132")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="8">
													<Accordion.Header>
														{t("Governance.key133")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key134")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="9">
													<Accordion.Header>
														{t("Governance.key135")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key136")}</p>
													</Accordion.Body>
												</Accordion.Item>

												<Accordion.Item eventKey="10">
													<Accordion.Header>
														{t("Governance.key137")}
													</Accordion.Header>
													<Accordion.Body>
														<p>{t("Governance.key138")}</p>
														<p>{t("Governance.key139")}</p>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										</div>
									}
								</div>
							}
							{
								section?.type === ReportSection.ROOT &&
								<div className='BackCover_card'>
									<Accordion defaultActiveKey={['0']} alwaysOpen>
										<Accordion.Item eventKey="0">
											<Accordion.Header>
												{t("BackCover.key0")}
											</Accordion.Header>
											<Accordion.Body>
												<p>{t("BackCover.key2")}</p>
											</Accordion.Body>
										</Accordion.Item>
									</Accordion>
								</div>
							}
						</div>
					</div>
				}</>

		);
	}

	const renderGraphColumn = (type, index) => {
		return (
			<div className={selectedGraph.type === 'KPI' ? 'ESG_performance_ShortValuesBOx' : 'p-2'}
				key={`${type}Graph-${selectedGraph.id}-${index}`}
				id={`${type}Graph-${selectedGraph.id}-${index}`}>
				<CompanyGraphCard
					key={`${type}Graph-${selectedGraph.id}-${index}`}
					isAutoGenerateGraphImg={true}
					exportRef={exportRef}
					year={yearFilter}
					config={selectedGraph}
					onExport={(graphImageURL) => addGraphImageToEditor(graphImageURL)} />
			</div>
		);
	};


	// Function to check if current page is a leadership message page
	const isLeadershipMessagePage = () => {
		if (!page || !page.name) return false;

		const leadershipPageNames = [
			'Letter from CEO/Chairman/MD',
			'Message from the Chief Sustainability Officer'
		];

		return leadershipPageNames.includes(page.name);
	};

	return (
		<>
			<div className='floating-actionSticky col-md-12 px-1 pb-2'>
				{getCardActions()}
			</div>
			<div className='col-md-12 ReportfileAndSection p-0'>
				<div
					ref={cardScrollRef}
					className={showFullView ? 'col-md-12 px-1' : 'ReportfileAndSectionIn02'}
				>
					{getPagetoRender()}
				</div>

				{/* <div className={showFullView ? 'd-none' : ''}> */}
				{/* Hide ESGAI Copilot for leadership message pages */}
				{!isLeadershipMessagePage() && (
					<div className={showFullView ? 'd-none' : 'RightSide_Tab AITextGenWithPrompt_tab'}>
						<div className='RightSide_Tab_head'>
							<div className='RightSide_Tab_head_in'>
								<div className='RTH_01esg'>
									<img src={esg_ai} width={15} height={15} />
									<h6 className='mb-0'>{t('ESGAICopilot.label')}</h6>
								</div>
								<div className='AIText_menu'>
									{/* <span className='cursor'>
									<BsArrowClockwise />
								</span> */}

									<Dropdown>
										<Dropdown.Toggle variant="AIGen_dotMenu" id="dropdown-basic">
											<BsThreeDotsVertical />
										</Dropdown.Toggle>

										<Dropdown.Menu>
											<Dropdown.Item href="#" onClick={(e) => { e.preventDefault(); }}>Privacy</Dropdown.Item>
											<Dropdown.Item href="https://ecodrisil.com/terms-of-use" target="_blank">Terms & Conditions</Dropdown.Item>
											{/* <Dropdown.Item href="javascript:void(0)" onClick={() => { setShowCopilotHistory(true) }}>Copilot History</Dropdown.Item> */}
										</Dropdown.Menu>
									</Dropdown>

									<span className='cursor' onClick={() => { setShowFullView(true) }}>
										<BsXLg />
									</span>

								</div>
								{loader && (
									<div className='spinner-Body'>
										<div className='spinner-8'></div>
									</div>

								)}

							</div>
						</div>
						<div>
							<AITextGenWithPrompt page={page}
								section={section}
								periodYear={periodYear}
								regulationId={regulationId}
								regulationName={regulationName}
								regulationFrequency={regulationFrequency}
								onHide={() => setShowAITextGen(false)}
								onTransferToEditor={(text, jsonData) => transferTextToEditor(text, jsonData)}
								jobId={jobId} reportingStandard={reportingStandard}
								language={language}
								copilotHistory={showCopilotHistory}
								hideCopilotHistory={(e) => setShowCopilotHistory(e)}
								reportName={reportName}
								periodStart={periodStart}
								periodEnd={periodEnd}
								entities={entities}
							/>
						</div>
					</div>
				)}
				{/* </div> */}
			</div >
			{
				selectedGraph &&
				<div ref={exportRef} className='invisible-position'>
					<div>{renderGraphColumn('esg', 0)}</div>
				</div>
			}
		</>
	);
}

export default withTranslation()(ReportingCard);
